import { useParams } from "react-router-dom"
import { organizationApi } from "@/api/client/organization"
import { SectionError } from "@/components/ui/error"
import { SectionLoading } from "@/components/ui/loading"
import { usePermission } from "@/features/auth/hooks/usePermission"
import { IpRestrictionForm } from "./components"

const ORGANIZATION_NOT_FOUND_MESSAGE = "組織が見つかりません。"

const isNotFoundError = (error: unknown): boolean =>
  typeof error === "object" && error !== null && (error as { status?: number }).status === 404

export function OrganizationIpRestrictionPage() {
  const { organizationId = "" } = useParams<{ organizationId: string }>()
  const { canUpdate, organizationId: ownOrganizationId } = usePermission()

  const {
    data: organizationData,
    isPending: isOrganizationPending,
    isError: isOrganizationError,
    refetch: refetchOrganizations,
  } = organizationApi.useOrganizationList()
  const {
    data: ipRestrictionData,
    isPending: isIpRestrictionPending,
    isError: isIpRestrictionError,
    error: ipRestrictionError,
    refetch: refetchIpRestriction,
  } = organizationApi.useOrganizationIpRestrictionGet(organizationId, {
    // 取り直すとフォームが初期化され、編集中の許可リストが消えるため、保存後だけ取り直す。
    query: { refetchOnWindowFocus: false },
  })

  if (isOrganizationPending || isIpRestrictionPending) {
    return <SectionLoading />
  }

  if (isOrganizationError || isIpRestrictionError) {
    return (
      <SectionError
        message={
          isNotFoundError(ipRestrictionError)
            ? ORGANIZATION_NOT_FOUND_MESSAGE
            : "IPアドレス制限の設定を取得できませんでした。権限が不足しているか、通信に失敗した可能性があります。"
        }
        onRetry={() => {
          refetchOrganizations()
          refetchIpRestriction()
        }}
      />
    )
  }

  const organization = organizationData.organizations.find(({ id }) => id === organizationId)
  if (!organization) {
    return (
      <SectionError
        message={ORGANIZATION_NOT_FOUND_MESSAGE}
        onRetry={() => refetchOrganizations()}
      />
    )
  }
  const { ipRestriction, currentIp } = ipRestrictionData

  return (
    <div>
      <h1 className="text-2xl font-bold mb-1">IPアドレス制限</h1>
      <p className="text-sm text-slate-500 mb-4">{organization.name}</p>

      <IpRestrictionForm
        // 保存後に取り直した設定でフォームを初期化し直す。
        key={ipRestriction.updatedAt ?? "unset"}
        organizationName={organization.name}
        ipRestriction={ipRestriction}
        currentIp={currentIp}
        isOwnOrganization={organizationId === ownOrganizationId}
        canEdit={canUpdate("organizations")}
        onSaved={() => {
          refetchIpRestriction()
          refetchOrganizations()
        }}
      />
    </div>
  )
}
